/**
 * Teams & Enterprise solution page.
 */
import { Shield, Users2, LayoutDashboard, Activity, KeyRound, Fingerprint } from "lucide-react";
import SolutionPageTemplate from "../SolutionPageTemplate";

export default function TeamsEnterpriseSolutionPage() {
  return (
    <SolutionPageTemplate
      slug="teams-enterprise"
      persona="teams, funds, and brokerages"
      eyebrow="Solutions · Teams &amp; Enterprise"
      title="Governance for real estate at scale."
      titleAccent="No spreadsheets. No black boxes."
      subtitle="Six-role RBAC. Immutable audit log. MFA + IP allowlist. Impersonation with a paper trail. Explainable AI. Built for teams that will be audited — funds, brokerages, family offices."
      bullets={[
        "6 roles × 26 capabilities matrix — no ad-hoc permission drift",
        "Every mutation optionally MFA-gated + IP-allowlisted (BP-026)",
        "Every AI recommendation cited with source engine + version",
      ]}
      metricStrip={[
        { stat: "6", label: "RBAC roles" },
        { stat: "26", label: "capabilities" },
        { stat: "∞", label: "audit retention" },
        { stat: "0", label: "black-box AI decisions" },
      ]}
      pains={[
        { title: "Permission drift", desc: "New hire gets \"admin\" temporarily. Six months later they still have it. Auditor asks why." },
        { title: "AI black boxes", desc: "Someone acted on an AI recommendation nobody can explain to the LP. Reputational risk." },
        { title: "No audit trail", desc: "Regulator asks 'who changed this offer price?' You can't answer. You lose the review." },
        { title: "Onboarding chaos", desc: "New acquisitions hires can't tell which strategy the team uses. Deals go rogue." },
      ]}
      playbook={[
        { step: 1, title: "Provision", desc: "6 roles × 26 capabilities. New user is scoped in 60 seconds.", bp: "BP-026" },
        { step: 2, title: "Govern", desc: "MFA + IP allowlist + retention policy per tenant. Regulator-ready.", bp: "BP-026" },
        { step: 3, title: "Explain", desc: "Every AI decision has evidence. Every metric has a source engine.", bp: "BP-025" },
        { step: 4, title: "Prove", desc: "Immutable audit log. Impersonation logged. Every offer, every draw, forever.", bp: "BP-002 · BP-026" },
      ]}
      productsUsed={[
        { icon: Shield, title: "Platform Governance", to: "/platform-overview", desc: "RBAC, MFA, IP allowlist, immutable audit — enterprise-grade from day one." },
        { icon: Users2, title: "Buyer Intelligence", to: "/products/buyer-intelligence", desc: "Institutional buyer network with proof-of-funds validation." },
        { icon: LayoutDashboard, title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Board-ready dashboards with cited engine versions and PDF exports." },
        { icon: Activity, title: "Property Workspace", to: "/products/property-workspace", desc: "Immutable timeline. Every draw, every clause, every signer — captured." },
        { icon: KeyRound, title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Explainable AI with confidence caps — never invents a number for the LP deck." },
        { icon: Fingerprint, title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "Institutional cash-flow, cap-rate, and DSCR aggregation across the fund." },
      ]}
      proof={{
        stat: "3×",
        label: "BI dashboards replaced by one platform",
        quote: "The Executive Dashboard replaced three BI dashboards we ran every Monday. Every number is cited. Our LP letters write themselves.",
        attribution: "Fund PM · Enterprise · 6 markets",
      }}
      related={[
        { title: "Wholesalers", to: "/solutions/wholesalers", desc: "Same governance, applied to high-velocity acquisition teams." },
        { title: "Buy & Hold", to: "/solutions/buy-and-hold", desc: "Institutional rental portfolio operations with full audit." },
        { title: "Creative Finance", to: "/solutions/creative-finance", desc: "Governance is table-stakes for creative structures." },
      ]}
    />
  );
}
